import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./DashboardDateRangePicker.module.css";
import IconComponent from "../../../components/Icon/IconComponent";
import { ICON_LABELS } from "../../../utils/constants/UI";
import { AutoCompleteCustom } from "../../../components/Autocomplete/AutoCompleteCustom";
import { MONTHS } from "../../../utils/constants";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

const PRESET_DESCRIPTIONS = {
  all_time: "Showing data across all recorded time, no date filter applied.",
  today: "Showing data for today only.",
  yesterday: "Showing data for yesterday only.",
  last_7_days: "Showing data for the past 7 days.",
  last_30_days: "Showing data for the past 30 days.",
};

dayjs.extend(isBetween);

const PRESETS = [
  { label: "All Time", value: "all_time" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom Range", value: "custom_range" },
];

const YEARS = [];
for (let i = 2020; i <= 2030; i++) {
  YEARS.push({ id: String(i), label: String(i) });
}

const DashboardDateRangePicker = ({ onSubmit, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activePreset, setActivePreset] = useState("all_time");

  // Selection state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Dual view navigation state
  const [leftViewDate, setLeftViewDate] = useState(dayjs());
  const [rightViewDate, setRightViewDate] = useState(dayjs().add(1, "month"));

  const [tempActivePreset, setTempActivePreset] = useState("all_time");

  const containerRef = useRef(null);

  const toggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTempActivePreset(activePreset);
      // Reset view to selection or current
      if (startDate) {
        setLeftViewDate(startDate.startOf("month"));
        setRightViewDate(startDate.add(1, "month").startOf("month"));
      }
    }
  };

  const handlePresetClick = (preset) => {
    setTempActivePreset(preset.value);
    if (preset.value !== "custom_range") {
      setActivePreset(preset.value);
      onSubmit({ label: preset.value });
      setIsOpen(false);
    }
  };

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (date.isBefore(startDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      setEndDate(date);
    }
  };

  const handleApply = () => {
    if (tempActivePreset === "custom_range" && startDate && endDate) {
      setActivePreset("custom_range");
      const payload = {
        label: "custom_range",
        from: startDate.startOf("day").valueOf(),
        to: endDate.endOf("day").valueOf(),
      };
      console.log("[DatePicker] custom_range applied:", {
        ...payload,
        from_local: new Date(payload.from).toString(),
        to_local: new Date(payload.to).toString(),
      });
      onSubmit(payload);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setTempActivePreset(activePreset);
  };

  const handleClickOutside = useCallback((event) => {
    // QA reporter: clicks inside the report dialog/button must not close popovers
    if (event.target?.closest?.("[data-qa-exclude]")) return;
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, handleClickOutside]);

  const getDisplayValue = () => {
    if (activePreset === "custom_range" && startDate && endDate) {
      return `${startDate.format("MMM D")} - ${endDate.format("MMM D")}`;
    }
    const preset = PRESETS.find((p) => p.value === activePreset);
    return preset ? preset.label : "Select Range";
  };

  // Calendar Grid Renderer
  const renderCalendarDays = (viewDate) => {
    const startOfMonth = viewDate.startOf("month");
    const endOfMonth = viewDate.endOf("month");
    const daysInMonth = viewDate.daysInMonth();
    const startDay = startOfMonth.day();

    const dayElements = [];

    // Empty prefix cells
    for (let i = 0; i < startDay; i++) {
      dayElements.push(
        <div
          key={`empty-${i}`}
          className={`${styles.dayCell} ${styles.dayEmpty}`}
        />,
      );
    }

    // Active day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const date = viewDate.date(d);
      const isStart = startDate && date.isSame(startDate, "day");
      const isEnd = endDate && date.isSame(endDate, "day");
      const isSelected = isStart || isEnd;
      const isInRange =
        startDate &&
        endDate &&
        date.isAfter(startDate, "day") &&
        date.isBefore(endDate, "day");

      let dayClass = styles.dayCell;
      if (isSelected) dayClass += ` ${styles.daySelected}`;
      if (isInRange) dayClass += ` ${styles.dayInRange}`;
      if (isStart && endDate) dayClass += ` ${styles.dayInRangeStart}`;
      if (isEnd && startDate) dayClass += ` ${styles.dayInRangeEnd}`;

      dayElements.push(
        <div
          key={`${viewDate.format("YYYY-MM")}-${d}`}
          className={dayClass}
          onClick={() => handleDateClick(date)}
          onMouseEnter={() => {}} // Could add hover span preview here
        >
          {d}
        </div>,
      );
    }

    return dayElements;
  };

  const CalendarInstance = ({ viewDate, setViewDate, side }) => (
    <div className={styles.calendarWrapper}>
      <div className={styles.calendarHeader}>
        {/* <div
          className={styles.navArrow}
          onClick={() => setViewDate(viewDate.subtract(1, "month"))}
        >
          <IconComponent iconLabel={ICON_LABELS.CHEVRON_LEFT} fontSize="20px" />
        </div> */}

        <div
          className={`${styles.containsAutoComplete} ${styles.monthSelector}`}
        >
          <AutoCompleteCustom
            dropdownId={`${side}-month-dropdown`}
            items={MONTHS}
            size="lg44"
            valueState={[MONTHS[viewDate.month()].label]}
            idState={[String(viewDate.month())]}
            onChange={(d) => setViewDate(viewDate.month(parseInt(d.id[0])))}
            disablePortal
          />
        </div>

        <div
          className={`${styles.containsAutoComplete} ${styles.yearSelector}`}
        >
          <AutoCompleteCustom
            dropdownId={`${side}-year-dropdown`}
            items={YEARS}
            size="lg44"
            valueState={[String(viewDate.year())]}
            idState={[String(viewDate.year())]}
            onChange={(d) => setViewDate(viewDate.year(parseInt(d.id[0])))}
            disablePortal
          />
        </div>

        {/* <di  */}
      </div>

      <div className={styles.calendarGrid}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div key={day} className={styles.dayHeader}>
            {day}
          </div>
        ))}
        {renderCalendarDays(viewDate)}
      </div>
    </div>
  );

  return (
    <div className={styles.pickerContainer} ref={containerRef}>
      <div className={styles.trigger} onClick={toggle}>
        <IconComponent
          iconLabel={ICON_LABELS.DATE_RANGE}
          fontSize="16px"
          style={{ color: "#5f5f61", flexShrink: 0 }}
        />
        <span className={styles.label}>Date Range</span>
        <span className={styles.triggerDivider}>·</span>
        <span className={styles.value}>{getDisplayValue()}</span>
        <IconComponent
          iconLabel={ICON_LABELS.KEYBOARD_ARROW_DOWN}
          fontSize="16px"
          style={{ color: "#5f5f61", flexShrink: 0 }}
        />
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.sidebar}>
            {PRESETS.map((p) => (
              <div
                key={p.value}
                className={`${styles.preset} ${tempActivePreset === p.value ? styles.presetActive : ""}`}
                onClick={() => handlePresetClick(p)}
              >
                {p.label}
              </div>
            ))}
          </div>

          <div className={styles.innerContent}>
            {tempActivePreset === "custom_range" ? (
              <>
                <div className={styles.calendarDualView}>
                  <CalendarInstance
                    viewDate={leftViewDate}
                    setViewDate={setLeftViewDate}
                    side="left"
                  />
                  <CalendarInstance
                    viewDate={rightViewDate}
                    setViewDate={setRightViewDate}
                    side="right"
                  />
                </div>

                <div className={styles.footer}>
                  <div className={styles.selectedRangeDisplay}>
                    <div className={styles.datePill}>
                      {startDate
                        ? startDate.format("MMM DD, YYYY")
                        : "Start Date"}
                    </div>
                    <div className={styles.rangeSeparator}>→</div>
                    <div className={styles.datePill}>
                      {endDate ? endDate.format("MMM DD, YYYY") : "End Date"}
                    </div>
                  </div>
                  <div className={styles.footerActions}>
                    <button className={styles.btnCancel} onClick={handleCancel}>
                      Cancel
                    </button>
                    <button
                      className={styles.btnApply}
                      onClick={handleApply}
                      disabled={!startDate || !endDate}
                    >
                      Apply Range
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.presetInfoPanel}>
                <div className={styles.presetInfoIcon}>
                  <IconComponent
                    iconLabel={ICON_LABELS.DATE_RANGE}
                    fontSize="22px"
                  />
                </div>
                <div className={styles.presetInfoTitle}>
                  {PRESETS.find((p) => p.value === tempActivePreset)?.label}
                </div>
                <div className={styles.presetInfoDesc}>
                  {PRESET_DESCRIPTIONS[tempActivePreset]}
                </div>
                <div className={styles.presetInfoHint}>
                  Select <strong>Custom Range</strong> to filter by a specific
                  date range.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDateRangePicker;
